import * as React from 'react';
import { PageSEOPropsType } from 'components/page-seo/page-seo';
import { Maybe, RootQueryToCategoryConnection, Scalars } from 'interfaces/microsite-cms';
import HeroComponent from 'components/success-stories/hero-block/hero-block';
import { PageIndexCategoryList } from 'components/page-index-category-list/page-index-category-list';
import { PageArchiveCategoryList } from 'components/page-archive-category-list/page-archive-category-list';
import { SupportedLanguage } from 'lib/lexemes/supported-languages';
import Breadcrumbs, { setBreadCrumbs } from 'components/breadcrumbs/breadcrumbs';
import { definePropsString } from 'lib/define-prop-strings';
import { useAmp } from 'next/amp';
import { PageCategoriesList } from 'components/more-category-posts/more-category-posts';
import { HeroBlockContextProvider } from 'components/success-stories/hero-block/hero-block.context-provider';
import { HeroBlockClipPath } from 'components/success-stories/hero-block/hero-block-clip-path';

export enum PageTemplateContentType {
    Index = 'Index',
    Archive = 'Archive',
    Article = 'Article',
}

type renderPageElementProps = {
    condition: boolean;
    children?: React.ReactNode;
};
function renderPageElement(props: renderPageElementProps) {
    return props.condition ? props.children : null;
}

type PageTemplateContentProps = {
    id?: Scalars['Int'];
    lang: SupportedLanguage;
    type: PageTemplateContentType;
    intro?: React.ReactNode;
    categories?: Maybe<RootQueryToCategoryConnection>;
    pageCategoriesList?: PageCategoriesList;
    children?: React.ReactNode;
    modal?: React.ReactNode;
    title?: string;
    browserTab?: string;
    seoTitle?: string;
    seoDescription?: string;
    seoUrl?: string;
    openTitle?: string;
    openDescription?: string;
    datePublished?: string;
    dateModified?: string;
    // @TODO: add support in CMS
    metaKeywords?: string | null;
    metaRobotsNoindex?: string | null;
    metaRobotsNofollow?: string | null;
    opengraphPublishedTime?: string | null;
    opengraphModifiedTime?: string | null;
    openImageUrl?: string | null;
    openImageAlt?: string | null;
    heroImageUrl?: string | null;
    heroImageAlt?: string | null;
    heroImageHeight?: string | null;
    heroImageWidth?: string | null;
    openSiteName?: string;
    hasFooterCta?: boolean;
    footerTitleText?: Maybe<string> | undefined;
    hasFooterCtaButton?: boolean;
    ctaButtonText?: Maybe<string> | undefined;
    ctaButtonUrl?: Maybe<string> | undefined;
    ctaButtonTargetNewWindow?: boolean;
    onPageClick?: () => void;
    dynamicFooter?: boolean;
    geoIpCountry?: string | null;
    pageUrl?: string;
    articleUrl?: string;
} & PageSEOPropsType;

function PageTemplateContent(props: PageTemplateContentProps) {
    const isAmp = useAmp();

    return (
        <HeroBlockContextProvider>
            {renderPageElement({
                condition: Boolean(props.type !== PageTemplateContentType.Article),
                children: props.heroImageUrl && (
                    <HeroComponent
                        priority={true}
                        type={HeroComponent.type.image}
                        url={props.heroImageUrl}
                        alt={props.heroImageAlt || props.openImageAlt || props.title}
                        fullWidth={true}
                        contentVerticalPosition={HeroComponent.contentVerticalAlignment.center}
                        contentHorizontalPosition={HeroComponent.contentHorizontalAlignment.center}
                        hasFrame={true}
                        mode={HeroComponent.mode.BFF}
                    >
                        {props.intro}
                    </HeroComponent>
                ),
            })}

            {props.children}

            {props.type === PageTemplateContentType.Index && props.pageCategoriesList ? (
                <PageIndexCategoryList
                    pageCategoriesList={props.pageCategoriesList}
                    backgroundColor="#fff"
                    hideTitle={true}
                />
            ) : null}

            {props.type === PageTemplateContentType.Archive && props.pageCategoriesList ? (
                <PageArchiveCategoryList
                    backgroundColor="#fff"
                    breadcrumbs={
                        <Breadcrumbs
                            list={setBreadCrumbs({
                                categories: props.categories,
                                name: props.title,
                                slug: props.pageUrl,
                            })}
                        />
                    }
                    pageCategoriesList={props.pageCategoriesList}
                    filter={definePropsString(props.title)}
                    titleUrl={props.pageUrl}
                />
            ) : null}

            {renderPageElement({
                condition: Boolean(props.type === PageTemplateContentType.Article && !isAmp),
                children: props.pageCategoriesList && (
                    <PageIndexCategoryList
                        pageCategoriesList={props.pageCategoriesList}
                        filter={props.id}
                        isRelatedArticles={true}
                        hideCta={true}
                    />
                ),
            })}
            <HeroBlockClipPath />
        </HeroBlockContextProvider>
    );
}
PageTemplateContent.type = PageTemplateContentType;

export default PageTemplateContent;
